import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Carpet Installation',
    tagline: 'Comfort, texture & precision fit',
    desc: 'Residential and commercial carpet installation handled end-to-end — from pad selection to final stretching — with expert craftsmanship.',
    image: 'https://public.readdy.ai/ai/img_res/9b57fe9fb6bd6aad88224d81fc25989b.jpg',
    icon: 'ri-layout-grid-line',
    href: '/services/carpet',
  },
  {
    title: 'Hardwood Flooring',
    tagline: 'Timeless beauty, lasting value',
    desc: 'Solid and engineered hardwood installed with precision — enhancing the beauty and long-term value of any residential or commercial space.',
    image: 'https://readdy.ai/api/search-image?query=premium%20hardwood%20flooring%20installation%20beautiful%20wood%20grain%20texture%20warm%20natural%20tones%20elegant%20home%20interior%20polished%20floor%20reflection%20high%20end%20residential%20luxury%20design&width=600&height=500&seq=service-hardwood-01&orientation=landscape',
    icon: 'ri-home-3-line',
    href: '/services/carpet',
  },
  {
    title: 'Laminate Flooring',
    tagline: 'Durability meets premium style',
    desc: 'High-performance laminate that replicates the look of hardwood or stone — installed flawlessly for a refined, polished finish.',
    image: 'https://readdy.ai/api/search-image?query=modern%20laminate%20flooring%20installation%20clean%20contemporary%20interior%20light%20wood%20effect%20elegant%20minimalist%20home%20design%20polished%20finish%20natural%20light%20high%20quality%20residential%20space&width=600&height=500&seq=service-laminate-01&orientation=landscape',
    icon: 'ri-stack-line',
    href: '/services/carpet',
  },
  {
    title: 'Vinyl Flooring',
    tagline: 'Waterproof, resilient, stylish',
    desc: 'Premium vinyl flooring ideal for high-traffic and moisture-prone areas. Combines durability with an elegant finish.',
    image: 'https://readdy.ai/api/search-image?query=luxury%20vinyl%20plank%20flooring%20installation%20modern%20kitchen%20bathroom%20interior%20waterproof%20flooring%20light%20neutral%20tones%20clean%20contemporary%20high%20end%20residential%20design%20bright&width=600&height=500&seq=service-vinyl-01&orientation=landscape',
    icon: 'ri-droplet-line',
    href: '/services/carpet',
  },
  {
    title: 'Specialty & Marine Carpet',
    tagline: 'Purpose-built performance',
    desc: 'Carpet tiles, marine-grade carpet, and specialty turf solutions engineered for unique environments and demanding applications.',
    image: 'https://readdy.ai/api/search-image?query=marine%20carpet%20specialty%20flooring%20boat%20interior%20high%20performance%20carpet%20tiles%20outdoor%20turf%20installation%20professional%20commercial%20space%20durable%20quality%20materials&width=600&height=500&seq=service-marine-01&orientation=landscape',
    icon: 'ri-anchor-line',
    href: '/services/carpet',
  },
  {
    title: 'Floor Replacement & Repairs',
    tagline: 'Restore. Renew. Refinish.',
    desc: 'From targeted repairs to full floor replacements, we restore your floors to pristine condition — quickly and professionally.',
    image: 'https://readdy.ai/api/search-image?query=professional%20floor%20repair%20restoration%20hardwood%20floor%20refinishing%20restoration%20home%20renovation%20quality%20craftsmanship%20elegant%20interior%20before%20after%20transformation%20skilled%20installation&width=600&height=500&seq=service-repair-01&orientation=landscape',
    icon: 'ri-tools-line',
    href: '/services/carpet',
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} id="services" className="py-24 lg:py-32 bg-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="max-w-xl">
            <span className="section-tag mb-4 block">Flooring Solutions</span>
            <h2 className="section-heading">
              Specialty Flooring Services<br />
              <em className="text-gold not-italic">for Every Space</em>
            </h2>
            <p className="text-warm-gray mt-5 text-base leading-relaxed">
              From plush residential carpet to marine-grade specialty flooring — we install, replace, and repair with precision and care.
            </p>
          </div>
          <button onClick={scrollToContact} className="btn-secondary-dark self-start lg:self-auto whitespace-nowrap flex items-center gap-2">
            Request Consultation
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, i) => (
            <Link
              key={service.title}
              to={service.href}
              className="group bg-white rounded-lg overflow-hidden block hover:-translate-y-1 transition-transform duration-300"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.6s ease ${0.08 + i * 0.08}s, transform 0.6s ease ${0.08 + i * 0.08}s`,
                boxShadow: '0 2px 16px rgba(44,44,44,0.06)',
              }}
            >
              <div className="relative overflow-hidden" style={{ height: '220px' }}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="w-8 h-8 flex items-center justify-center bg-gold rounded-md">
                    <i className={`${service.icon} text-charcoal text-sm`}></i>
                  </span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-2">{service.tagline}</p>
                <h3 className="font-serif text-xl text-charcoal mb-3 group-hover:text-gold transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-warm-gray text-sm leading-relaxed mb-4">{service.desc}</p>
                <span className="text-gold text-sm font-semibold flex items-center gap-1.5 group-hover:gap-2.5 transition-all duration-200">
                  Learn More
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA strip below grid */}
        <div
          className="mt-10 bg-charcoal rounded-lg px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-5"
          style={{
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.7s ease 0.5s',
          }}
        >
          <div>
            <p className="font-serif text-white text-lg mb-1">Not sure which flooring is right for your space?</p>
            <p className="text-white/50 text-sm">Our specialists will guide you through every option.</p>
          </div>
          <button onClick={scrollToContact} className="btn-primary whitespace-nowrap flex-shrink-0">
            Free Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
